import { useNavigate } from 'react-router-dom';
import { Button } from '@components/primitives/Button';
import { FeatureCard } from '@components/ui/cards/FeatureCard';
import { useEventTracking } from '@services/statsig';

import { useNotesToReview } from './hooks/useNotesToReview';
import { CountBadge } from './CountBadge';
import { NotesList } from './NotesList';

/**
 * Container/Composer component for Notes to Review
 * Orchestrates data fetching, navigation, and event tracking
 * All business logic lives here, sub-components are pure UI
 */
export const NotesToReviewCard = () => {
  const navigate = useNavigate();
  const { dashboard } = useEventTracking();
  const { notes, noteCounts, loading } = useNotesToReview();

  // Navigation handlers
  const handleNoteClick = (_noteId: string, link: string) => {
    dashboard.actionClicked({
      action_type: 'note_clicked',
      source: 'dashboard_v2_notes_card',
    });
    navigate(link, { state: { origin: 'dashboard' } });
  };

  const handleViewAll = () => {
    dashboard.actionClicked({
      action_type: 'view_all_notes',
      source: 'dashboard_v2_notes_card',
    });
    navigate('/notes');
  };


  // Toggle between overlay button and in-list button
  const showActionButtonOverlay = false; // Set to true to show as overlay at bottom
  const showViewAllInList = !showActionButtonOverlay && notes && notes.length > 0;

  return (
    <FeatureCard
      title="Notes to Review"
      badge={<CountBadge count={noteCounts?.for_review_notes_count} />}
      action={
        showActionButtonOverlay && notes && notes.length > 0 ? (
          <Button onClick={handleViewAll} variant="outlined">
            Review All Notes ({noteCounts?.for_review_notes_count || 0})
          </Button>
        ) : undefined
      }
      fullHeight
      sx={{ gridColumn: { xs: '1', md: 'span 2' } }}
      contentSx={{
        p: 0,
        maxHeight: 400,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <NotesList
        notes={notes}
        onNoteClick={handleNoteClick}
        onViewAllNotes={handleViewAll}
        onViewAll={showViewAllInList ? handleViewAll : undefined}
        loading={loading}
      />
    </FeatureCard>
  );
};
